import { apiPathToUrlWithToken } from '@lib/network/api';
import { userStore } from '@stores/User';

/** `GET /rendering/compilation/v2/{renderId}` as the API sends it: the render document, fields by lifecycle stage. */
type CompilationWire = {
  _id: string;
  width?: number;
  height?: number;
  /** Absent on renders saved before the field existed, which were only ever saved on success. */
  status?: 'completed' | 'failed';
  /**
   * FIXME(atlas-render-schema): `metadata` is not declared on atlas's
   * `RenderingValidation.ProjectSchema`; it reaches us only because that
   * schema is `.unknown(true)`, so the producer's contract is implicit. The
   * editor writes `title`, `session_id`, `library_id`, `user_id`, `render_id`.
   * Two readers depend on it today: this page (`title`) and monorepo.vibedit's
   * video-share worker (`cloudflare-workers/video-share/src/video.ts`, from
   * `/share/data`). Acceptable once: the field is stable and both fall back
   * to a default. Retires when atlas declares `metadata` on `ProjectSchema`,
   * at which point this comment goes and the type mirrors the schema.
   */
  project?: { metadata?: { title?: string } };
};

/** What a page needs to say which render it is about. */
export type CompilationInfo = {
  id: string;
  width: number | null;
  height: number | null;
  /** True when the render failed: there is no file, and the API refuses to schedule it. */
  failed: boolean;
  /** The project's title as the editor saved it; null when the render carries none. */
  title: string | null;
};

const toCompilationInfo = (wire: CompilationWire): CompilationInfo => {
  if (!wire._id) throw new Error('compilation without _id');
  return {
    id: wire._id,
    width: wire.width ?? null,
    height: wire.height ?? null,
    failed: wire.status === 'failed',
    title: wire.project?.metadata?.title || null,
  };
};

/** What a media element needs to play a render: the file and a frame to show before it. */
export type CompilationMedia = { video: string; poster: string };

/**
 * `GET .../video` and `.../thumbnail` answer a 302 to the file in storage
 * (a day's presigned URL), so a plain `<video>` streams it with Range
 * requests. Null without a session. Built once per render and held: the
 * session token rotates, and a source string that follows it would reset
 * the player mid-playback.
 */
export const compilationMedia = (
  renderId: string,
  token: string | null,
): CompilationMedia | null => {
  const video = apiPathToUrlWithToken(`/rendering/compilation/v2/${renderId}/video`, token);
  const poster = apiPathToUrlWithToken(`/rendering/compilation/v2/${renderId}/thumbnail`, token);
  return video && poster ? { video, poster } : null;
};

/** One render the user owns. 404 `rendering.notFound` for an unknown id, 403 `rendering.forbidden` for another user's. */
export const fetchCompilationInfo = async (renderId: string): Promise<CompilationInfo> =>
  toCompilationInfo(
    await userStore.apiWithAuth<CompilationWire>(`/rendering/compilation/v2/${renderId}`),
  );
